import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Box,
  Grid,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import React, { useState } from "react";
import { IconPlus, IconTrash } from "@tabler/icons";
import PropTypes from "prop-types";

function TableForm({
  table,
  setTable,
  handleOpenMaster,
  setSelectedRow,
  header,
  setHeader,
  addReceipt,
  resetState,
  fetchRecieptNo,
  deleteReceipt,
  readOnly = false,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleChange = (index, field, value) => {
    const updated = [...table];
    updated[index][field] = value;
    setTable(updated);
  };

  const handleAddRow = () => {
    setTable([
      ...table,
      {
        account_id: "",
        account_name: "",
        division: "",
        amount: "",
      },
    ]);
  };

  // Remove row
  const handleRemoveRow = (index) => {
    if (table.length === 1) return; // prevent deleting last row
    const updated = table.filter((_, i) => i !== index);
    setTable(updated);
  };

  return (
    <Paper sx={{ mt: 2, p: 2, borderRadius: 2 }}>
      <TableContainer>
        <Table size="small">
          {/* ✅ HEADER */}
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#324537",
                "& th": {
                  fontWeight: 600,
                  fontSize: "13px",
                  color: "#FFF",
                  borderBottom: "1px solid #e5e7eb",
                  py: 1,
                },
              }}
            >
              <TableCell width="55%">Account Name</TableCell>
              <TableCell width="25%">Amount</TableCell>
              <TableCell width="20%">Type</TableCell>
            </TableRow>
          </TableHead>

          {/* ✅ BODY */}
          <TableBody>
            {table.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  "& td": {
                    borderBottom: "1px solid #f0f0f0",
                    py: 1,
                  },
                  "&:hover": {
                    backgroundColor: "#fafafa",
                  },
                }}
              >
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.account_name || ""}
                    onClick={() => {
                      if (!readOnly && handleOpenMaster) {
                        setSelectedRow(index);
                        handleOpenMaster();
                      }
                    }}
                    disabled={readOnly}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    value={row.amount || ""}
                    onChange={(e) =>
                      handleChange(index, "amount", e.target.value)
                    }
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={row.transactionType || "Cash"}
                    onChange={(e) => handleChange(index, "transactionType", e.target.value)}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Card">Card</option>
                    <option value="FAWRAN">FAWRAN</option>
                    <option value="Bank">Bank</option>
                    {row.transactionType === "RTGS" && <option value="RTGS">RTGS</option>}
                  </TextField>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box my={2}>
        <Divider />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            name="narration"
            value={header.narration}
            onChange={(e) =>
              setHeader((prev) => ({ ...prev, narration: e.target.value }))
            }
            type="text"
            size="small"
            fullWidth
            label="Narration"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <Grid mt={2} container spacing={2}>
        <Grid item xs={12} sm={3}>
          <Button
            onClick={addReceipt}
            sx={{
              bgcolor: "#324537",
              color: "#FFF",
              "&:hover": {
                bgcolor: "#243328",
              },
              "&.Mui-disabled": {
                bgcolor: "#f1f5f9",
                color: "#94a3b8",
              }
            }}
            fullWidth
          >
            Submit
          </Button>
        </Grid>
        {!readOnly && (
          <>
            <Grid item xs={12} sm={3}>
              <Button
                sx={{
                  bgcolor: "#b91c1c",
                  color: "#FFF",
                  "&:hover": {
                    bgcolor: "#991b1b",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#f1f5f9",
                    color: "#94a3b8",
                  }
                }}
                fullWidth
                onClick={() => setConfirmOpen(true)}
              >
                Delete
              </Button>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                sx={{
                  bgcolor: "#b39536",
                  color: "#FFF",
                  "&:hover": {
                    bgcolor: "#927623",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#f1f5f9",
                    color: "#94a3b8",
                  }
                }}
                fullWidth
                onClick={() => {
                  fetchRecieptNo();
                  resetState();
                }}
              >
                Clear
              </Button>
            </Grid>
          </>
        )}
      </Grid>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "8px",
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#1e293b", pb: 1 }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#475569", fontSize: "14px" }}>
            Are you sure you want to delete Receipt No. <strong>#{header?.rec_no}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            sx={{
              color: "#475569",
              borderColor: "#cbd5e1",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#94a3b8",
                bgcolor: "#f8fafc",
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              deleteReceipt();
            }}
            variant="contained"
            sx={{
              bgcolor: "#b91c1c",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                bgcolor: "#991b1b",
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default TableForm;

TableForm.propTypes = {
  setTable: PropTypes.any,
  table: PropTypes.any,
  handleOpenMaster: PropTypes.any,
  setSelectedRow: PropTypes.any,
  header: PropTypes.any,
  setHeader: PropTypes.any,
  addReceipt: PropTypes.any,
  fetchRecieptNo: PropTypes.any,
  resetState: PropTypes.any,
  deleteReceipt: PropTypes.any,
  fetchReceipt: PropTypes.any,
  readOnly: PropTypes.bool,
};
